import { GiftCardTransactionsFragment } from '@/gql/generated';
import { formatDateToTimezone } from '@/lib/timezone';
import { formatNumberToCurrency } from '@/lib/utils';
import { paymentMethods, transactionsType } from '@/utils/constant';
import { formatCodeGiftCard } from '@/utils/gift-card';
import { ReceiptText } from 'lucide-react';
import { useContext, useState } from 'react';
import TopNavigation from '../../_components/top-navigation';
import { AuthenticatedContext } from '../../../_provider/authenticated-provider';
import TransactionReceiptModal from '../receipt/page';

const Item = (props: { type: string; title: string[] }) => {
  return (
    <>
      <p className="font-bold">{props.type}</p>
      <div className="flex flex-col gap-1">
        {props.title.map((x) => (
          <p key={x}>{x}</p>
        ))}
      </div>
    </>
  );
};

const TransactionDetailModal = (props: {
  transaction: GiftCardTransactionsFragment;
  onClose: () => void;
  setShowReceipt: (show: boolean) => void;
}) => {
  const { user } = useContext(AuthenticatedContext);
  const paymentMethod = props.transaction.payment_methods?.[0];

  return (
    <div className="fixed z-[100] w-full h-full bg-white">
      <header className="py-10 text-gray-primary shadow-[0px_2px_8px_0px_#0000001A]">
        <TopNavigation
          title="Gift card transaction details"
          onBack={props.onClose}
          actions={<></>}
        />
      </header>

      <section className="px-24 pt-9 text-2xl">
        <div className="grid grid-cols-3 items-center pb-6 font-bold">
          <p>Type</p>
          <p
            className={
              props.transaction.type === transactionsType.REDEMPTION
                ? 'text-danger-discount'
                : 'text-success'
            }
          >
            {props.transaction.type === transactionsType.REDEMPTION
              ? 'Redeem'
              : 'Add funds'}
          </p>
          <div
            className="flex h-14 w-44 cursor-pointer items-center gap-3 rounded-xl border border-silver bg-white px-5 text-2xl font-bold"
            onClick={() => {
              props.setShowReceipt(true);
            }}
          >
            <ReceiptText className="!size-8" />
            Receipt
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1 py-3">
          <Item
            type="Summary"
            title={['Date and time', 'Gift card number', 'Transaction ID']}
          />
          <div className="flex flex-col gap-1">
            <p>
              {formatDateToTimezone(
                props.transaction.date,
                user?.time_zone!,
                'LLL d, yyyy hh:mm a',
              )}
            </p>
            <p>
              {props.transaction.gift_card?.pos_gift_card?.code
                ? formatCodeGiftCard(
                    props.transaction.gift_card?.pos_gift_card?.code,
                    false,
                  )
                : formatCodeGiftCard(
                    props.transaction?.gift_card?.code ?? '',
                    true,
                  )}
            </p>
            <p>{props.transaction?.code}</p>
          </div>
        </div>

        <div className="my-6 border border-gray-border"></div>
        <div className="grid grid-cols-3 gap-1 pb-6">
          {props.transaction.type === transactionsType.TOP_UP ? (
            <>
              <Item
                type="Gift card balance"
                title={['Amount', 'Current balance', 'Discount', 'Amount Paid']}
              />
              <div className="flex flex-col gap-1 font-bold">
                <p className="text-success">
                  +{formatNumberToCurrency(props.transaction.balance_change)}
                </p>
                <p className="pl-4">
                  {formatNumberToCurrency(props.transaction.balance_after)}
                </p>
                <p className="pl-2 text-danger-discount">
                  -
                  {formatNumberToCurrency(
                    ((props.transaction.transaction_discount?.value ?? 0) *
                      (props.transaction.balance_change ?? 0)) /
                      10000,
                  )}
                  <span>
                    {' '}
                    (
                    {(props.transaction.transaction_discount?.value ?? 0) / 100}
                    %)
                  </span>
                </p>
                <p className="pl-4">
                  {formatNumberToCurrency(props.transaction.price ?? 0)}
                </p>
              </div>
            </>
          ) : (
            <>
              <Item
                type="Gift card balance"
                title={[
                  'Current balance',
                  'Redeem amount',
                  'Remaining balance',
                ]}
              />
              <div className="flex flex-col gap-1 font-bold">
                <p className="pl-2">
                  {formatNumberToCurrency(props.transaction.balance_before)}
                </p>
                <p className="text-danger-discount">
                  -{formatNumberToCurrency(props.transaction.balance_change)}
                </p>

                <p className="pl-3">
                  {formatNumberToCurrency(props.transaction.balance_after ?? 0)}
                </p>
              </div>
            </>
          )}
        </div>

        {(paymentMethod?.type === paymentMethods.CHARGE ||
          paymentMethod?.type === paymentMethods.MANUAL_CARD_ENTRY) && (
          <div className="grid grid-cols-3 gap-1 py-6">
            <Item
              type="Payment method"
              title={['ID', 'Cardholder name', 'Card type', 'Card last four']}
            />
            <div className="flex flex-col gap-1">
              <p className="flex-none truncate">
                ID{props.transaction.code || '/'}
              </p>
              <p>{paymentMethod?.card_info?.cardholder_name || '/'}</p>
              <p>{paymentMethod?.card_info?.card_type || '/'}</p>
              <p>{paymentMethod?.card_info?.card_number_last_four || '/'}</p>
            </div>
          </div>
        )}
        {paymentMethod?.type === paymentMethods.CASH && (
          <div className="grid grid-cols-3 gap-1 py-6">
            <Item type="Payment method" title={['ID', 'Type']} />
            <div className="flex flex-col gap-1">
              <p className="flex-none truncate">
                ID{props.transaction.code || '/'}
              </p>
              <p className="capitalize">{paymentMethod?.type || '/'}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-1 py-6">
          <Item type="Customer" title={['Name', 'Phone number']} />
          <div className="flex flex-col gap-1">
            <p>
              {props.transaction.gift_card?.customer_company?.name || 'Unknown'}
            </p>
            <p>
              {props.transaction.gift_card?.customer_company?.customer
                ?.phone_number || '/'}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

const GiftCardTransactionDetailModal = (props: {
  transaction: GiftCardTransactionsFragment;
  onClose: () => void;
}) => {
  const [showReceipt, setShowReceipt] = useState(false);
  return (
    <>
      <TransactionDetailModal
        transaction={props.transaction}
        onClose={props.onClose}
        setShowReceipt={setShowReceipt}
      />
      {showReceipt && (
        <TransactionReceiptModal
          transaction={props.transaction}
          onClose={() => {
            setShowReceipt(false);
          }}
        />
      )}
    </>
  );
};

export default GiftCardTransactionDetailModal;
